import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const FormContainer = styled.div`
  width: 100%;
  max-width: 500px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Input = styled.input`
  font-family: "Big Caslon", serif;
  padding: 12px;
  width: 100%;
  font-size: 0.9rem;
  font-weight: bold;
  border: 1px solid #000;
  border-top: 1px solid #e0e0e0;
  color: #000 !important;
  background-color: #fff !important;
  
  &::placeholder {
    color: #ccc !important;
  }

  &:disabled {
    background-color: #f5f5f5 !important;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  font-family: "Big Caslon", serif;
  padding: 12px;
  width: 100%;
  min-height: 150px;
  resize: vertical;
  font-size: 0.9rem;
  font-weight: bold;
  border: 1px solid #000;
  border-top: 1px solid #e0e0e0;
  color: #000 !important;
  background-color: #fff !important;
  
  &::placeholder {
    color: #ccc !important;
  }

  &:disabled {
    background-color: #f5f5f5 !important;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  border: 1px solid #000;
  border-top: 1px solid #000;
  padding: 0px 16px;
  height: 37px;
  cursor: pointer;
  font-family: "Big Caslon", serif;
  font-size: 1rem;
  font-weight: normal;
  transition: all 0.3s ease;
  align-self: flex-start;
  background-color: transparent;
  color: #000;

  &:hover:not(:disabled) {
    background-color: #333;
    color: #fff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const StatusMessage = styled.p`
  margin: 8px 0;
  font-family: "Big Caslon", serif;
  font-size: 1.5rem;
  color: ${props => props.error ? '#ff0000' : '#ffffff'};
`;

const ContactForm = ({ onSuccess, buttonClassName }) => {
  const [status, setStatus] = useState('initial');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    message: ''
  });

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.message) {
      setErrorMessage('Please complete all required fields');
      return false;
    }
    if (!/[^@]+@[^.@]+\.[^@]+/.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setStatus('error');
      return;
    }
    
    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...formData
        })
      });

      if (response.ok) {
        setStatus('done');
        setFormData({ email: '', message: '' });
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Could not send message. Please try again later.');
      setStatus('error');
    }
  };

  const getButtonText = () => {
    switch (status) {
      case 'sending':
        return 'sending...';
      case 'done':
        return 'success!';
      case 'error':
        return 'error :(';
      default:
        return 'send';
    }
  };

  return (
    <FormContainer>
      <Form
        name="contact"
        method="POST"
        data-netlify="true"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="form-name" value="contact" />
        
        <Input
          type="email"
          name="email"
          placeholder="email"
          value={formData.email}
          onChange={handleChange}
          disabled={status === 'sending'}
          required
        />
        
        <TextArea
          name="message"
          placeholder="message"
          value={formData.message}
          onChange={handleChange}
          disabled={status === 'sending'}
          required
        />
        
        <Button
          type="submit"
          disabled={status === 'sending'}
          className={buttonClassName}
        >
          {getButtonText()}
        </Button>

        {errorMessage && (
          <StatusMessage error>{errorMessage}</StatusMessage>
        )}
        {status === 'done' && (
          <StatusMessage>Thank you for your message!</StatusMessage>
        )}
      </Form>
    </FormContainer>
  );
};

ContactForm.propTypes = {
  onSuccess: PropTypes.func
};

export default ContactForm; 